import { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { riskColorMapping, SolidGaugeChart } from './ClimateCharts';
import { extractHeatmapData, extractRiskIncreaseLines, extractSectorList } from 'src/util/util';
import { Heatmap } from './HeatMapChart';
import { LineChart } from './LineChart';
import ProjectOverview from '../../resources/images/Project_overview.png';
import EnvPara from '../../resources/images/envparameter.png';
import { ProjectInfoCard } from '../components/cardComponent';
import { useAppState } from '../../state-management/state-provider';
import { EnvironmentalSiteAnalysis, Site, UserRole } from 'src/api/Api';
import SiteMapWithLayer from '../DMap/SiteMapWithLayer';
import { useParams } from 'react-router-dom';
import { EnvironmentalRiskCategory, SampleProjectId } from 'src/enum';
import { roundToTwoDecimalPlaces } from 'src/util/util';
import Highcharts from 'highcharts';

export default function EnvAnalysisDetail() {
  const { state } = useAppState();
  const { selectedProject, selectedSite, user } = state;
  const { environmentalSiteAnalysis, projectDetails } = selectedProject;

  const [envAnalysisData, setEnvAnalysisData] = useState<EnvironmentalSiteAnalysis>();
  const [site, setSite] = useState<Site>();
  const { siteId } = useParams();

  // Two independent dropdown states
  const [selectedSectorProductivity, setSelectedSectorProductivity] = useState<string>('');
  const [selectedSectorDamage, setSelectedSectorDamage] = useState<string>('');
  const [selectedSectorLine, setSelectedSectorLine] = useState<string>('');
  const [sectors, setSectors] = useState<string[]>([]);

    const isUserRole = useMemo(() => {
      if (!user?.role || user.role.length === 0) return false;
      // Show only if user has ONLY User role (no Admin or Employee)
      const roles = user.role;
      const hasUserRole = roles.includes(UserRole.User);
      const hasAdminRole = roles.includes(UserRole.Admin);
      const hasEmployeeRole = roles.includes(UserRole.Employee);
      // Only show if user has User role and doesn't have Admin or Employee
      return hasUserRole && !hasAdminRole && !hasEmployeeRole;
    }, [user]);
    const isSampleProject = projectDetails?.project_id === SampleProjectId;
    // Disable exporting when project is the sample project AND the current user
    // has only the "User" role. Otherwise allow exporting.
    const allowExporting = !(isSampleProject && isUserRole);
    console.log('Is User Role:', isUserRole, allowExporting);
    // Ensure Highcharts global exporting option follows our allowExporting flag
    useEffect(() => {
      try {
        Highcharts.setOptions({
          exporting: {
            enabled: allowExporting,
          },
        });
      } catch (e) {
        // Ignore if Highcharts isn't ready; this is defensive
        console.warn('Failed to set Highcharts exporting option', e);
      }
    }, [allowExporting]);
  
  useEffect(() => {
    // @ts-ignore
    const found: EnvironmentalSiteAnalysis | undefined = environmentalSiteAnalysis?.find(
      (i: EnvironmentalSiteAnalysis) => i.site_id === selectedSite?.site_id,
    );

    const foundSite: Site | undefined = projectDetails?.sites?.find(
      (s) => s.site_id === selectedSite?.site_id,
    );

    setSite(foundSite);
    setEnvAnalysisData(found);
  }, [siteId, environmentalSiteAnalysis]);

  // --------------------------
  // Load Sector Names
  // --------------------------
  useEffect(() => {
    if (!envAnalysisData?.chronic_risks) return;

    const scenarios = envAnalysisData.chronic_risks.risk_scenarios;
    const sectorNames = extractSectorList(scenarios);

    setSectors(sectorNames);

    // Set defaults only once when data loads
    if (sectorNames.length > 0) {
      setSelectedSectorProductivity(sectorNames[0]);
      setSelectedSectorDamage(sectorNames[0]);
      setSelectedSectorLine(sectorNames[0]);
    }
  }, [envAnalysisData]);

  return (
    <Box sx={{ mt: 4 }}>
      {/* MAP */}
      {site && (
        <Box className="bio-map" sx={{ mt: 2 }} height={600}>
          <SiteMapWithLayer sites={[site]} fitToBoundary={true} />
        </Box>
      )}

      {/* MAIN HEADER */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          border: '1px solid #ccc',
          backgroundColor: '#fff',
          margin: '10px 1px',
          borderRadius: '8px',
        }}
        display="flex"
      >
        {/* LEFT CARD */}
        <Grid size={2} sx={{ flexShrink: 0 }}>
          <ProjectInfoCard
            imageSrc={ProjectOverview}
            title="Details"
            data={[
              {
                label: 'Total Area',
                value: site?.area
                  ? `${roundToTwoDecimalPlaces(site.area)} ${projectDetails?.area_unit}`
                  : null,
              },

              {
                label: 'Site Risk Score',
                value: envAnalysisData?.site_risk_score || null,
                color:
                  riskColorMapping[
                    envAnalysisData?.site_risk_category as EnvironmentalRiskCategory
                  ],
              },
              {
                label: 'Value at Risk',
                value: envAnalysisData?.site_value_at_risk
                  ? `${envAnalysisData.site_value_at_risk} $`
                  : null,
                color:
                  riskColorMapping[
                    envAnalysisData?.site_risk_category as EnvironmentalRiskCategory
                  ],
              },
            ]}
          />
        </Grid>

        {/* CENTER */}
        <Grid size={2} sx={{ padding: 1, paddingTop: 0, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              component="img"
              src={EnvPara}
              sx={{ width: '45px', height: '35px', objectFit: 'contain' }}
            />
            <Typography sx={{ fontWeight: 600, fontSize: '18px', p: 2, paddingBottom: 2 }}>Overall Risk</Typography>
          </Box>

          <Box sx={{ borderTop: '1px solid #D9D9D9', mt: 1 }} />

          <Box mt={2}>
            <Typography
              sx={{
                color: '#666',
                fontWeight: 600,
                fontSize: '16px',
                textAlign: 'center',
              }}
            >
              Risk Category
            </Typography>

            <SolidGaugeChart
              title=""
              category={envAnalysisData?.site_risk_category as EnvironmentalRiskCategory}
              riskLevelTitle={envAnalysisData?.site_risk_category}
              isSampleProject={isSampleProject}
              isUserRole={isUserRole}
            />
          </Box>
        </Grid>

        {/* RIGHT COLUMN (empty for now) */}
        <Grid sx={{ flexGrow: 1 }}></Grid>
      </Box>

      {/* CHRONIC RISK */}
      <Box sx={{ mb: 2 }}>
        <Grid className="project-box">
          <Typography sx={{ fontWeight: 600, fontSize: '22px', m: 2 }}>Chronic Risk</Typography>

          <Box sx={{ borderTop: '1px solid #D9D9D9' }} />

          {envAnalysisData?.chronic_risks && (
            <Box sx={{ mt: 3, p: 2 }}>
              {/* ----------------------------- */}
              {/* ROW 1 — PRODUCTIVITY */}
              {/* ----------------------------- */}
              <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
                Productivity Value at Risk (%)
              </Typography>

              <Typography sx={{ fontWeight: 500, mt: 1 }}>
                Select Asset Type (Productivity)
              </Typography>

              <select
                value={selectedSectorProductivity}
                onChange={(e) => setSelectedSectorProductivity(e.target.value)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #ccc',
                  marginTop: '6px',
                }}
              >
                {sectors.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>

              {/* Productivity Heatmaps */}
              <Box sx={{ mt: 2, display: 'flex', gap: 3 }}>
                {envAnalysisData.chronic_risks.risk_scenarios.map((scenario, idx) => {
                  const { years, hazards, matrix } = extractHeatmapData(
                    scenario,
                    selectedSectorProductivity,
                    'productivity_var_percent',
                  );

                  return (
                    <Box key={idx} sx={{ width: '33%' }}>
                      <Heatmap
                        title={`${scenario.scenario} — ${selectedSectorProductivity}`}
                        years={years}
                        hazards={hazards}
                        data={matrix}
                        exporting={allowExporting}
                      />
                    </Box>
                  );
                })}
              </Box>
              {/* ----------------------------- */}
              {/* ROW 2 — ASSET DAMAGE */}
              {/* ----------------------------- */}
              <Typography sx={{ fontWeight: 600, fontSize: '18px', mt: 5 }}>
                Asset Damage Value at Risk (%)
              </Typography>

              <Typography sx={{ fontWeight: 500, mt: 1 }}>Select Asset Type (Damage)</Typography>

              <select
                value={selectedSectorDamage}
                onChange={(e) => setSelectedSectorDamage(e.target.value)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #ccc',
                  marginTop: '6px',
                }}
              >
                {sectors.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>

              {/* Damage Heatmaps */}
              <Box sx={{ mt: 2, display: 'flex', gap: 3 }}>
                {envAnalysisData.chronic_risks.risk_scenarios.map((scenario, idx) => {
                  const { years, hazards, matrix } = extractHeatmapData(
                    scenario,
                    selectedSectorDamage,
                    'asset_damage_var_percent',
                  );

                  return (
                    <Box key={idx} sx={{ width: '33%' }}>
                      <Heatmap
                        title={`${scenario.scenario} — ${selectedSectorDamage} — Damage`}
                        years={years}
                        hazards={hazards}
                        data={matrix}
                        exporting={allowExporting}
                      />
                    </Box>
                  );
                })}
              </Box>

              {/* ----------------------------- */}
              {/* ROW 3 — RISK INCREASE LINE CHART */}
              {/* ----------------------------- */}

              <Typography sx={{ fontWeight: 600, fontSize: '18px', mt: 6 }}>
                Risk Increase Percent (Line Chart)
              </Typography>
              <select
                value={selectedSectorLine}
                onChange={(e) => setSelectedSectorLine(e.target.value)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #ccc',
                  marginTop: '6px',
                }}
              >
                {sectors.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <Box sx={{ mt: 2, display: 'flex', gap: 3 }}>
                {envAnalysisData.chronic_risks.risk_scenarios.map((scenario, idx) => {
                  const { years, series } = extractRiskIncreaseLines(scenario, selectedSectorLine);

                  return (
                    <Box key={idx} sx={{ width: '33%' }}>
                      <LineChart
                        title={`${scenario.scenario} — ${selectedSectorDamage}`}
                        years={years}
                        series={series}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Grid>
      </Box>
    </Box>
  );
}
